import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import type { Editor as TinyMCEEditor } from 'tinymce';

// Type definitions from TinyMCE
interface BlobInfo {
    blob: () => Blob;
}

type ProgressFn = (percent: number) => void;

interface ImageUploadHandler {
    (blobInfo: BlobInfo, progress: ProgressFn): Promise<string>;
}

// Import TinyMCE globally
import 'tinymce';

// Theme and icons
import 'tinymce/themes/silver';
import 'tinymce/icons/default';

// Models
import 'tinymce/models/dom';

// Plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';

interface Props {
    value: string;
    onChange: (value: string) => void;
    error?: string;
}

interface ContentError {
    message: string;
}

export const RichTextEditor: React.FC<Props> = ({ value, onChange, error }) => {
    const [contentError, setContentError] = useState<ContentError | null>(null);
    const editorRef = useRef<TinyMCEEditor>();

    const getEditorContent = (): string => {
        if (!editorRef.current) {
            return '';
        }
        return editorRef.current.getContent();
    };
    const lastValidContent = useRef<string>(value);

    const CONTENT_SIZE_LIMIT = 100 * 1024;

    const handleContentSize = (content: string) => {
        const encoder = new TextEncoder();
        const size = encoder.encode(content).length;
        
        if (size > CONTENT_SIZE_LIMIT) {
            setContentError({
                message: 'Content size exceeds 100kB limit'
            });
            return false;
        }
        
        setContentError(null);
        lastValidContent.current = content;
        return true;
    };

    const decodeHtmlEntities = (html: string): string => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = html;
        return textarea.value;
    };

    const handleBeforeAddingImage: ImageUploadHandler = async (blobInfo, progressFn) => {
        const blob = blobInfo.blob();
        console.log('Image upload handler called with blob:', blob.size, 'bytes');
        
        return new Promise<string>((resolve, reject) => {
            if (blob.size > CONTENT_SIZE_LIMIT) {
                reject('Image too large. Please use an image smaller than 100kB');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const base64String = e.target?.result as string;
                const currentContent = getEditorContent();
                const newContent = currentContent + `<img src="${base64String}" />`;
                
                if (handleContentSize(newContent)) {
                    progressFn(100);
                    resolve(base64String);
                } else {
                    reject('Adding this image would exceed the 100kB content limit');
                }
            };
            reader.onerror = () => reject('Failed to read image file');
            reader.onprogress = (e) => {
                if (e.lengthComputable) {
                    progressFn((e.loaded / e.total) * 100);
                }
            };
            reader.readAsDataURL(blob);
        });
    };

    const handleEditorChange = (newContent: string, editor: TinyMCEEditor) => {
        console.log('Editor content changed');
        if (handleContentSize(newContent)) {
            onChange(newContent);
        } else {
            const bookmark = editor.selection.getBookmark(2, true);
            editor.setContent(lastValidContent.current);
            editor.selection.moveToBookmark(bookmark);
        }
    };

    const handlePastePreProcess = (e: any) => {
        console.log('PastePreProcess - original content:', e.content.substring(0, 100));
        
        let processedContent = e.content;
        
        if (processedContent.includes('&lt;')) {
            processedContent = decodeHtmlEntities(processedContent);
            console.log('PastePreProcess - decoded content:', processedContent.substring(0, 100));
        }

        const currentContent = getEditorContent();
        const newContent = currentContent + processedContent;
        
        if (!handleContentSize(newContent)) {
            e.preventDefault();
            e.content = '';
            return;
        }
        
        e.content = processedContent;
    };

    const handleBeforePaste = () => {
        setContentError(null);
    };

    return (
        <Box sx={{ mb: 4 }}>
            <Editor
                onInit={(_evt, editor) => {
                    console.log('Editor initialized');
                    editorRef.current = editor;
                }}
                value={value}
                onEditorChange={handleEditorChange}
                init={{
                    base_url: '/tinymce',
                    height: 500,
                    menubar: false,
                    plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                        'preview', 'anchor', 'searchreplace', 'visualblocks', 'code',
                        'fullscreen', 'insertdatetime', 'media', 'table', 'wordcount',
                        'emoticons'
                    ],
                    toolbar: [
                        'styles fontfamily | bold italic underline strikethrough superscript subscript | forecolor backcolor',
                        'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table image emoticons code'
                    ],
                    toolbar_mode: 'wrap',
                    toolbar_sticky: true,
                    skin: 'oxide-dark',
                    content_css: 'dark',
                    content_style: `
                        body { 
                            background-color: #1B2B3A; 
                            color: white; 
                            font-family: Arial, sans-serif;
                            font-size: 14px;
                            line-height: 1.5;
                            padding: 16px;
                        }
                        table { border-collapse: collapse; width: 100%; }
                        td, th { border: 1px solid rgba(255, 255, 255, 0.2); padding: 5px; }
                    `,
                    style_formats: [
                        {
                            title: 'Headings',
                            items: [
                                { title: 'Heading 1', format: 'h1' },
                                { title: 'Heading 2', format: 'h2' },
                                { title: 'Heading 3', format: 'h3' },
                                { title: 'Heading 4', format: 'h4' },
                                { title: 'Heading 5', format: 'h5' },
                                { title: 'Heading 6', format: 'h6' }
                            ]
                        },
                        {
                            title: 'Blocks',
                            items: [
                                { title: 'Paragraph', format: 'p' },
                                { title: 'Blockquote', format: 'blockquote' },
                                { title: 'Div', format: 'div' },
                                { title: 'Pre', format: 'pre' }
                            ]
                        }
                    ],
                    font_family_formats: 'Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Courier New=courier new,courier; Georgia=georgia,palatino; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva',
                    context_menu: 'image link inserttable | cell row column deletetable',
                    paste_data_images: true,
                    paste_as_text: false,
                    paste_webkit_styles: 'all',
                    paste_merge_formats: true,
                    images_upload_handler: handleBeforeAddingImage,
                    automatic_uploads: false,
                    root_block: 'div',
                    setup: (editor) => {
                        editor.on('BeforePaste', handleBeforePaste);
                        editor.on('PastePreProcess', handlePastePreProcess);
                        editor.on('PastePostProcess', (e) => {
                            console.log('PastePostProcess event:', e);
                        });
                    },
                    valid_elements: '*[*]',
                    valid_children: '+body[style],+div[*],+table[*],+tr[*],+td[*],+img[src|alt|width|height]',
                    extended_valid_elements: '*[*]',
                    custom_elements: '*',
                    branding: false,
                    statusbar: false,
                    table_style_by_css: true,
                    table_use_colgroups: true,
                    table_default_styles: {
                        'border-collapse': 'collapse',
                        'width': '100%'
                    },
                    image_dimensions: true,
                    image_description: true,
                    image_title: true,
                    fontsize_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
                    textpattern_patterns: [
                        {start: '*', end: '*', format: 'italic'},
                        {start: '**', end: '**', format: 'bold'},
                        {start: '#', format: 'h1'},
                        {start: '##', format: 'h2'},
                        {start: '###', format: 'h3'},
                        {start: '####', format: 'h4'},
                        {start: '#####', format: 'h5'},
                        {start: '######', format: 'h6'},
                        {start: '1. ', cmd: 'InsertOrderedList'},
                        {start: '* ', cmd: 'InsertUnorderedList'},
                        {start: '- ', cmd: 'InsertUnorderedList'}
                    ],
                    licenseKey: 'gpl'
                }}
            />
            <Box sx={{ minHeight: '24px', mt: 1 }}>
                {(error || contentError) && (
                    <Typography 
                        sx={{ 
                            color: 'rgba(255, 82, 82, 0.87)',
                            fontSize: '0.75rem'
                        }}
                    >
                        {contentError?.message || error}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};